import React, { useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BoxPage } from '@/components/common'
import { Card } from '@/@core/components/mui/Card'
import { CardContent, IconButton, Typography, Box, Avatar, Chip, Divider, Grid, LinearProgress } from '@mui/material'
import UserStatusDropDown from '@/components/user/UserStatusDropDown'
import { useNotification } from '@/hooks/useNotification'
import {
  User,
  UserStatusEnum,
  useUpdateUserStatusMutation,
  useUpdateAvatarMutation,
  useSubmitDeleteUserMutation,
  Transaction,
  ActivityLogTypeEnum
} from '@/@data'
import { Button } from '@/@core/components/mui/Button'
import { AttendanceEmployees } from '@/components/user/employee/AttendanceEmployee'
import { TransactionTableFilter } from '@components/transaction'
import GuestAuthEmployee from '@/components/user/modal/GuestAuthEmployee'
import { useTranslator } from '@/hooks/useTranslator'
import { getFullName } from '@/utils/getInitials'
import { TabButton } from '@/components/common/button/TabButton'
import { NoteTimelineDialog } from '@/components/user/modal/NoteTimelineDialog'
import EmployeeEditForm from '@/components/user/employee/EmployeeEditForm'
import EmployeeEditPhoneForm from '@/components/user/employee/EmployeeEditPhoneForm'
import { formatDateDisplay } from '@/utils/formatDate'
import { useRouteQuery } from '@/hooks'
import TransactionDetailTableTransaction from '@/components/transaction/TransactionDetailTableTransaction'
import dayjs from 'dayjs'
import AppLogs from '@/components/common/AppLogs'
import { EmployeeConfirmRequestDeleteDialog } from '@/components/user/employee/EmployeeConfirmRequestDelete'

const statusBgColor: Record<string, string> = {
  active: '#D4F4E2',
  waiting: '#FFECD9',
  inactive: '#EAEAEC',
  guest: '#DFEBFF',
  blocked: '#FFDBDC',
  guest_waiting: '#DFEBFF',
  disable: '#EAEAEC',
  waiting_delete: '#FFDBDC'
}

const EmployeeDetail = () => {
  const { id } = useParams<{ id: string }>()
  const { __ } = useTranslator()
  const [tabValue, setTabValue] = useState(0)
  const { searchQuery } = useRouteQuery()
  const n = useNotification()
  const [guestModalOpen, setGuestModalOpen] = useState(false)
  // const [isOpenUserLogDrawer, setIsOpenUserLogDrawer] = useState(false)
  const [isOpenNote, setIsOpenNote] = useState(false)
  const [openSetting, setopenSetting] = useState(false)
  const [isCollapse, setIsCollapse] = useState(false)
  const updateAvatarMutation = useUpdateAvatarMutation()
  const submitDeleteUserMutation = useSubmitDeleteUserMutation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isOpenLogs, setIsOpenLogs] = useState(false)

  const [openChangePhone, setopenChangePhone] = useState(false)

  const formRef = useRef<{ submit: () => void; reset: () => void }>(null)

  const [openConfirm, setOpenConfirm] = useState(false)

  // Fetch employee data
  const { data: employee, refetch } = useQuery({
    ...User.objects.findOneQueryFactory(Number(id)),
    enabled: !!id
  })

  const navigate = useNavigate()

  const today = dayjs()
  const startDate = employee?.request_delete_at ? dayjs(new Date(employee.request_delete_at)) : null

  const totalDays = 30
  const passedDays = startDate ? Math.min(Math.max(today.diff(startDate, 'day') + 1, 0), totalDays) : 0
  const remainingDays = Math.max(totalDays - passedDays, 0)
  const progressPercentage = totalDays > 0 ? Math.min(Math.max((passedDays / totalDays) * 100, 0), 100) : 0

  const getProgressBarColor = (percentage: number) => {
    if (percentage >= 100) {
      return '#ff4c51' // Red for 100%
    } else if (percentage >= 70) {
      return '#ff9f43' // Orange for 70-99%
    } else {
      return '#3551F2' // Blue for 0-69%
    }
  }

  const paramsEmployee = useMemo(() => {
    return {
      page: searchQuery.get('page'),
      perPage: searchQuery.get('perPage'),
      user_keyword: searchQuery.get('user_keyword'),
      status: searchQuery.get('status'),
      user_id: id,
      created_from: searchQuery.get('created_from'),
      created_to: searchQuery.get('created_to')
    }
  }, [searchQuery.toString()])

  const { data: transactionEmployeeData } = useQuery({
    ...Transaction.getCompanyTransactionsEmployeeByCompanyId(employee?.company_id, paramsEmployee),
    enabled: !!id && !!employee?.company_id
  })

  const changeStatusMutation = useUpdateUserStatusMutation()

  if (!employee) return null

  const tabs = [
    {
      label: __('Transaction'),
      icon: <i className='tabler-cash' />
    },
    {
      label: __('Attendance/Payroll'),
      icon: <i className='tabler-calendar-event' />
    }
  ]

  const showTabs =
    [UserStatusEnum.ACTIVE, UserStatusEnum.BLOCKED, UserStatusEnum.DISABLE, UserStatusEnum.WAITING_DELETE].includes(
      employee.status
    ) && !openSetting

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file && employee) {
      updateAvatarMutation.mutate(
        {
          id: employee.id,
          avatar: file
        },
        {
          onSuccess: () => {
            refetch?.()
            n.success(__('Avatar updated successfully'))
          },
          onError: error => {
            n.error(__('Failed to update avatar: ') + error.message)
          }
        }
      )
    }
  }

  return (
    <>
      <BoxPage
        showBackButton={true}
        title={__('Employee Detail')}
        breadcrumbs={[
          { label: __('Users Management'), href: '/cms/users/employee' },
          { label: __('Employee Management'), href: '/cms/users/employee' },
          { label: __('Employee Detail') }
        ]}
      >
        <Grid container spacing={5}>
          <Grid size={openSetting && !isCollapse ? 10 : 12} className='left-layout'>
            <Card
              sx={{
                borderRadius: 3,
                mb: 6,
                backgroundColor: statusBgColor[employee.status?.toLowerCase()] || '#F9F9F9'
              }}
            >
              <CardContent>
                <Box className='flex items-star justify-between mb-4'>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                    <Avatar
                      src={employee.avatar_file?.tiny_url || employee.avatar}
                      alt={getFullName(employee)}
                      sx={{ width: 80, height: 80 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
                          {getFullName(employee)}
                        </Typography>

                        {employee.status === UserStatusEnum.GUEST ||
                        employee.status === UserStatusEnum.GUEST_WAITING ? (
                          <Button
                            variant='contained'
                            className='bg-[#6F83F6]'
                            endIcon={<i className='tabler-link' />}
                            sx={{ height: '30px', fontSize: '13px' }}
                            onClick={() => setGuestModalOpen(true)}
                          >
                            {employee.status === UserStatusEnum.GUEST_WAITING ? __('Guest Waiting') : __('Guest')}
                          </Button>
                        ) : (
                          <UserStatusDropDown
                            status={employee.status}
                            statusPrevious={employee.status_previous}
                            onSelect={status => {
                              changeStatusMutation.mutate(
                                {
                                  id: [employee.id],
                                  status: status as UserStatusEnum
                                },
                                {
                                  onSuccess: () => {
                                    refetch()
                                    n.success(__('Status updated successfully'))
                                  },
                                  onError: err => {
                                    if (err.message) {
                                      n.error(__(err.message))

                                      return
                                    }

                                    n.error(__('Status update failed'))
                                  }
                                }
                              )
                            }}
                            sx={{
                              height: '30px',
                              fontSize: '13px'
                            }}
                            excludeGuestStatuses={true}
                          />
                        )}

                        {showTabs && (
                          <Chip
                            sx={{ borderRadius: 3, color: '#fff' }}
                            label={employee.eligibility_rate + __('%')}
                            color='warning'
                            size='small'
                            icon={<i className='tabler-cash' />}
                            className='px-2 h-[30px]'
                          />
                        )}

                        {(employee.status === UserStatusEnum.GUEST ||
                          employee.status === UserStatusEnum.GUEST_WAITING ||
                          employee.status === UserStatusEnum.INACTIVE) && (
                          <IconButton
                            size='large'
                            className='bg-[#6F83F6] w-[38px] h-[28px] rounded-[20px]'
                            onClick={() => setIsOpenNote(true)}
                          >
                            <i className='tabler-note w-[10px] h-[16px] text-[#ffffff]' />
                          </IconButton>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='text-[#2F2B3D66]'>
                        <i className='tabler-id ' />
                        <span className='text-[15px] leading-[22px]'>{employee.system_code}</span>
                        <i
                          className='tabler-copy'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            navigator.clipboard.writeText(employee.system_code || '')
                            n.success(__('Copied to clipboard!'))
                          }}
                        />
                      </Box>
                      {openSetting && (
                        <Box className='flex items-center gap-2 mt-2'>
                          <Button
                            onClick={handleUploadClick}
                            variant='outlined'
                            className='gap-2 rounded-full bg-[#ffffff] w-[200px] py-[15px] text-[13px]'
                            sx={{
                              height: 30
                            }}
                          >
                            {__('Upload New Photo')}
                          </Button>
                          <input
                            type='file'
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept='image/*'
                            className='hidden'
                          />

                          <Typography variant='body2' className='text-[#2F2B3D66] text-[13px] flex w-full '>
                            {__('At least 800x800 px recommended JPG or PNG is allowed')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    {employee.status !== UserStatusEnum.GUEST && employee.status !== UserStatusEnum.GUEST_WAITING && (
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        onClick={() => {
                          if (openSetting) {
                            setopenSetting(false)
                            setIsCollapse(false)
                          } else {
                            setopenSetting(true)
                            setIsCollapse(false)
                            setopenChangePhone(false)
                          }
                        }}
                      >
                        <i
                          className={`tabler-${openSetting ? 'x' : 'settings'} text-[#2f2b3de6]`}
                          style={{ fontSize: 18 }}
                        />
                      </IconButton>
                    )}

                    {!openSetting && (
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        // onClick={() => {
                        //   navigate(`/cms/users/employee/${employee.id}/activity-log`)
                        // }}
                        onClick={() => setIsOpenLogs(true)}
                      >
                        <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    {/* open save */}
                    {isCollapse && (
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        onClick={() => {
                          setIsCollapse(false)
                        }}
                      >
                        <i className='tabler-viewport-narrow text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                      </IconButton>
                    )}

                    {/* close save */}
                    {!isCollapse && openSetting && (
                      <IconButton
                        size='small'
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#e0e0e0'
                          }
                        }}
                        onClick={() => {
                          setIsCollapse(true)
                        }}
                      >
                        <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {employee.status === 'waiting_delete' && (
                  <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#F9F9F9' }}>
                    <Box className='flex items-center justify-between'>
                      <Typography className='text-[18px] leading-[28px] font-bold'>
                        {__('User Data Deletion Request')}
                      </Typography>
                      <Box className='flex items-center justify-between gap-6'>
                        <Button
                          variant='contained'
                          className='text-[#ffffff] shadow-none rounded-full'
                          color='error'
                          disabled={submitDeleteUserMutation.isPending}
                          onClick={() => {
                            submitDeleteUserMutation.mutate(
                              {
                                id: employee.id,
                                name: 'reject'
                              },
                              {
                                onSuccess: () => {
                                  n.error(__('Delete request rejected'))
                                  refetch?.()
                                  navigate('/cms/users/employee')
                                },
                                onError: error => {
                                  n.error(error?.message ? __(error.message) : __('Reject failed'))
                                }
                              }
                            )
                          }}
                        >
                          {submitDeleteUserMutation.isPending ? __('Rejecting...') : __('Reject')}
                        </Button>
                        <Button
                          variant='contained'
                          className='text-[#ffffff] bg-[#28C76F] shadow-none rounded-full'
                          onClick={() => setOpenConfirm(true)}
                          disabled={passedDays < 30}
                        >
                          {__('Confirm Request')}
                        </Button>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 4 }} />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box className='border border-[#2F2B3D1A] pr-6 pl-6 py-4 h-full rounded'>
                          <Box className='flex items-center justify-between gap-6'>
                            <Box className='flex items-center justify-between gap-2'>
                              <Typography className='text-[18px] leading-[22px] text-[#2F2B3DE5]'>
                                {__('Request')}
                              </Typography>
                              <Typography className='text-[18px] leading-[22px] text-[#2F2B3DE5]'>
                                {'#' + employee?.id || '-'}
                              </Typography>
                            </Box>
                            <Box className='flex items-center justify-between gap-2'>
                              <Typography className='text-[15px] leading-[22px] text-[#2F2B3D66]'>
                                {__('Date Request:')}
                              </Typography>
                              <Typography className='text-[15px] leading-[22px] text-[#2F2B3D66]'>
                                {formatDateDisplay(employee?.request_delete_at) || '-'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box className='flex'>
                            <Typography className='text-[15px] leading-[22px] text-[#2F2B3DB2]'>
                              {__('Reason: ')}
                            </Typography>
                            <Typography className='text-[15px] leading-[22px] text-[#2F2B3DB2]'>
                              {employee?.extra?.reason_delete || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box className='flex flex-col gap-3 border border-[#2F2B3D1A] pr-6 pl-6 py-4 rounded'>
                          <Box className='flex justify-between items-center'>
                            <Typography className='text-[15px] leading-[22px] font-bold text-[#2F2B3D]'>
                              {__('Days')}
                            </Typography>
                            <Typography className='text-[15px] leading-[22px] font-bold text-[#2F2B3D]'>
                              {passedDays} {__('of')} {totalDays} {__('Days')}
                            </Typography>
                          </Box>

                          <LinearProgress
                            variant='determinate'
                            value={progressPercentage}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: '#E0E0E0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getProgressBarColor(progressPercentage),
                                borderRadius: 4
                              }
                            }}
                          />
                          <Typography className='text-[15px] leading-[22px] font-normal text-[#2F2B3D99]'>
                            {remainingDays} {__('days remaining until this account will be deleted from the system.')}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                )}

                <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#F9F9F9' }}>
                  <Typography className='text-[18px] text-[#2F2B3DE5] font-bold'>
                    {openChangePhone ? __('Change Phone/ID') : __('Personal Info')}
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  {openSetting ? (
                    <>
                      {openChangePhone ? (
                        <EmployeeEditPhoneForm
                          ref={formRef}
                          employee={employee}
                          onSuccess={() => {
                            refetch?.()
                            setopenSetting(false)
                            setIsCollapse(false)
                          }}
                        />
                      ) : (
                        <EmployeeEditForm
                          ref={formRef}
                          employee={employee}
                          onSuccess={() => {
                            refetch?.()
                            setopenSetting(false)
                            setIsCollapse(false)
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <div className='grid gap-5'>
                      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Employee Name')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {getFullName(employee)}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Employer')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.company?.name || employee?.company_name || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Employee Code')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.employee_id || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Joined Date')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {formatDateDisplay(employee?.joined_date) || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Contract Type')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.contract_type || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Bank Account Name')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.bank_account_name || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Bank Name')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.bank?.shortName || '-'} - {employee?.bank?.name || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Bank Account')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.bank_account_number || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Identity Card')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.identity_card || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Identity Card Issue Date')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {formatDateDisplay(employee?.identity_card_issue_date) || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Identity Card Issue Place')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.identity_card_issue_place || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Gender')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.gender || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Phone')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.phone_code || '+84'} {employee?.phone || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('DOB')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {formatDateDisplay(employee?.birth_date) || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Dependent')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.dependent || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Tenure')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.joined_date
                              ? `${dayjs().diff(dayjs(employee.joined_date), 'month')} ${__('months')}`
                              : employee?.tenure || '-'}
                          </Typography>
                        </Box>

                        <Box className='flex flex-col'>
                          <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                            {__('Address')}:
                          </Typography>
                          <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                            {employee?.address || '-'}
                          </Typography>
                        </Box>
                      </div>
                    </div>
                  )}
                </Card>
              </CardContent>
            </Card>
          </Grid>
          {!isCollapse && openSetting && (
            <Grid size={2} className='right-layout'>
              <Card
                sx={{
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  p: 6
                }}
              >
                <Button
                  variant='contained'
                  className='text-[15px] font-medium leading-[22px] bg-[#6F83F629] text-[#6F83F6] shadow-none rounded-full'
                  onClick={() => {
                    formRef.current?.reset()
                  }}
                >
                  {__('Reset')}
                </Button>

                {openChangePhone ? (
                  <Button
                    variant='contained'
                    type='submit'
                    startIcon={<i className='tabler-phone-check' />}
                    className='text-[#28C76F] text-[15px] font-medium leading-[22px] bg-[#28C76F29] shadow-none rounded-full'
                    onClick={() => formRef.current?.submit()}
                  >
                    {__('Change phone')}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant='contained'
                      type='submit'
                      className='text-[#28C76F] text-[15px] font-medium leading-[22px] bg-[#28C76F29] shadow-none rounded-full'
                      onClick={() => formRef.current?.submit()}
                    >
                      {__('Save')}
                    </Button>
                    <Button
                      variant='contained'
                      type='submit'
                      className='text-[#FF9F43] text-[15px] font-medium leading-[22px] bg-[#FF9F4329] shadow-none rounded-full'
                      onClick={() => setopenChangePhone(true)}
                    >
                      {__('Change Phone')}
                    </Button>
                  </>
                )}
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Custom Tabs */}
        {showTabs && (
          <>
            <Box sx={{ mb: 6, display: 'flex', gap: 1 }}>
              {tabs.map((tab, index) => (
                <TabButton
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  isSelected={tabValue === index}
                  onClick={() => setTabValue(index)}
                />
              ))}
            </Box>

            <Card
              sx={{
                borderRadius: 3
              }}
            >
              <CardContent sx={{ padding: 0 }}>
                {tabValue === 0 && (
                  <>
                    <TransactionTableFilter
                      columnVisibility={{}}
                      onColumnVisibilityChange={() => {}}
                      isTransaction={false}
                    />

                    <TransactionDetailTableTransaction
                      data={transactionEmployeeData?.data || []}
                      meta={(transactionEmployeeData as any)?.meta}
                      isTransaction={false}
                    />
                  </>
                )}
                {tabValue === 1 && (
                  <>
                    <AttendanceEmployees employee={employee} />
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </BoxPage>

      <GuestAuthEmployee
        open={guestModalOpen}
        onClose={() => setGuestModalOpen(false)}
        employee={employee}
        refetch={refetch}
      />
      <NoteTimelineDialog open={isOpenNote} setOpen={setIsOpenNote} employee={employee} />

      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Employee Detail')}
        object_type={ActivityLogTypeEnum.User}
        objectId={employee.id}
      />

      <EmployeeConfirmRequestDeleteDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        employee={employee}
        onSuccess={() => {
          refetch?.()
        }}
      />
    </>
  )
}

export default EmployeeDetail
